import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  language,
  isOpen,
  onToggle 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const countryOptions = [
    { value: 'ethiopia', label: language === 'en' ? 'Ethiopia' : '埃塞俄比亚' },
    { value: 'nigeria', label: language === 'en' ? 'Nigeria' : '尼日利亚' },
    { value: 'uganda', label: language === 'en' ? 'Uganda' : '乌干达' },
    { value: 'kenya', label: language === 'en' ? 'Kenya' : '肯尼亚' }
  ];

  const processingOptions = [
    { value: 'washed', label: language === 'en' ? 'Washed' : '水洗' },
    { value: 'natural', label: language === 'en' ? 'Natural' : '日晒' },
    { value: 'honey', label: language === 'en' ? 'Honey' : '蜜处理' },
    { value: 'semi-washed', label: language === 'en' ? 'Semi-Washed' : '半水洗' }
  ];

  const certificationOptions = [
    { value: 'organic', label: language === 'en' ? 'Organic' : '有机认证' },
    { value: 'fairtrade', label: language === 'en' ? 'Fair Trade' : '公平贸易' },
    { value: 'rainforest', label: language === 'en' ? 'Rainforest Alliance' : '雨林联盟' },
    { value: 'utz', label: language === 'en' ? 'UTZ Certified' : 'UTZ认证' }
  ];

  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = new Date()?.getFullYear() - i;
    return { value: year?.toString(), label: year?.toString() };
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleArrayFilterChange = (key, value, checked) => {
    const currentArray = localFilters?.[key] || [];
    let newArray;
    
    if (checked) {
      newArray = [...currentArray, value];
    } else {
      newArray = currentArray?.filter(item => item !== value);
    }
    
    handleFilterChange(key, newArray);
  };

  const handleClearAll = () => {
    const clearedFilters = {
      search: '',
      countries: [],
      priceMin: '',
      priceMax: '',
      processing: [],
      elevationMin: '',
      elevationMax: '',
      productionYear: '',
      certifications: []
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
  };

  const activeFilterCount = Object.values(localFilters)?.filter(value => {
    if (Array.isArray(value)) return value?.length > 0;
    return value !== '' && value !== null && value !== undefined;
  })?.length;

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-6">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          className="w-full"
        >
          {language === 'en' ? 'Filters' : '筛选器'}
          {activeFilterCount > 0 && (
            <span className="ml-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>
      {/* Filter Panel */}
      <div className={`
        lg:block bg-card border border-border rounded-lg p-6 space-y-6
        ${isOpen ? 'block' : 'hidden'}
        lg:sticky lg:top-20
      `}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            {language === 'en' ? 'Filters' : '筛选器'}
          </h3>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              iconName="X"
              iconPosition="left"
            >
              {language === 'en' ? 'Clear All' : '清除全部'}
            </Button>
          )}
        </div>

        {/* Search */}
        <div>
          <Input
            label={language === 'en' ? 'Search Beans' : '搜索咖啡豆'}
            type="search"
            placeholder={language === 'en' ? 'Bean name or farm name...' : '咖啡豆名称或农场名称...'}
            value={localFilters?.search || ''}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
          />
        </div>

        {/* Countries */}
        <div>
          <Select
            label={language === 'en' ? 'Origin Countries' : '产地国家'}
            options={countryOptions}
            value={localFilters?.countries || []}
            onChange={(value) => handleFilterChange('countries', value)}
            multiple
            searchable
            placeholder={language === 'en' ? 'Select countries...' : '选择国家...'}
          />
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            {language === 'en' ? 'Price Range (USD/kg)' : '价格范围 (美元/公斤)'}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder={language === 'en' ? 'Min' : '最低'}
              value={localFilters?.priceMin || ''}
              onChange={(e) => handleFilterChange('priceMin', e?.target?.value)}
              min="0"
              step="0.01"
            />
            <Input
              type="number"
              placeholder={language === 'en' ? 'Max' : '最高'}
              value={localFilters?.priceMax || ''}
              onChange={(e) => handleFilterChange('priceMax', e?.target?.value)}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {/* Processing Methods */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            {language === 'en' ? 'Processing Methods' : '处理方法'}
          </label>
          <div className="space-y-2">
            {processingOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                checked={(localFilters?.processing || [])?.includes(option?.value)}
                onChange={(e) => handleArrayFilterChange('processing', option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Elevation Range */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            {language === 'en' ? 'Elevation (meters)' : '海拔高度 (米)'}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder={language === 'en' ? 'Min' : '最低'}
              value={localFilters?.elevationMin || ''}
              onChange={(e) => handleFilterChange('elevationMin', e?.target?.value)}
              min="0"
            />
            <Input
              type="number"
              placeholder={language === 'en' ? 'Max' : '最高'}
              value={localFilters?.elevationMax || ''}
              onChange={(e) => handleFilterChange('elevationMax', e?.target?.value)}
              min="0"
            />
          </div>
        </div>

        {/* Production Year */}
        <div>
          <Select
            label={language === 'en' ? 'Production Year' : '生产年份'}
            options={yearOptions}
            value={localFilters?.productionYear || ''}
            onChange={(value) => handleFilterChange('productionYear', value)}
            placeholder={language === 'en' ? 'Select year...' : '选择年份...'}
            clearable
          />
        </div>

        {/* Certifications */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            {language === 'en' ? 'Certifications' : '认证'}
          </label>
          <div className="space-y-2">
            {certificationOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                checked={(localFilters?.certifications || [])?.includes(option?.value)}
                onChange={(e) => handleArrayFilterChange('certifications', option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Mobile Close Button */}
        <div className="lg:hidden pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onToggle}
            fullWidth
          >
            {language === 'en' ? 'Apply Filters' : '应用筛选器'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;